import {
  ReachCampaignDetails,
  SourcingPurchaseDetails,
} from 'api-clients/monolith';
import _round from 'lodash/round';
import { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { HiringTarget } from '@/components/pages/edit-campaign/use-hiring-target';

import { CampaignParameters } from './types';

export interface ReachSummary {
  budget?: number;
  expectedCpa?: number;
  expectedConversion?: number;
  targetApplicants?: number;
  targetHires?: number;
  perDayBudget?: number;
}

interface UseReachSummaryProps {
  hiringTarget: HiringTarget;
  campaign?: SourcingPurchaseDetails;
  selectedPublisher?: ReachCampaignDetails;
  hookForm: UseFormReturn<CampaignParameters>;
}

export function useReachSummary({
  hiringTarget,
  campaign,
  selectedPublisher,
  hookForm,
}: UseReachSummaryProps) {
  const campaignExpectedCpa = campaign?.expected_cpa ?? 0;
  const campaignExpectedConversion = campaign?.expected_conversion ?? 0;
  const noOfCampaigns = campaign?.reach_campaigns.length ?? 0;

  const selectedPublisherExpectedCpa = selectedPublisher?.expected_cpa ?? 0;
  const selectedPublisherExpectedConversion =
    selectedPublisher?.expected_conversion ?? 0;

  const {
    targetBudget,
    initialTargetBudget,
    remainingDurationBudget,
    overlapDuration,
  } = hiringTarget;

  const { watch } = hookForm;
  const inputBudget = watch('budget') || 0;
  const inputExpectedCpa = watch('expected_cpa') || 0;
  const inputExpectedConversion = watch('expected_conversion') || 0;

  /**
   * Calculates the campaign budget based on the remaining duration budget and the budget spent.
   *
   * Formula:
   * newBudget = total budget of all publishers - initial target budget of selected publisher + target budget of selected publisher
   * total budget of all publishers = sum of remaining duration budget for all publishers + budget spent
   */
  const budget = useMemo(() => {
    const currentBudget =
      campaign?.reach_campaigns.reduce((acc, publisher) => {
        const remainingDuration = overlapDuration(
          publisher.starts_at,
          publisher.end_at ?? undefined,
        );

        return (
          acc +
          remainingDurationBudget('daily', remainingDuration, publisher.budget)
        );
      }, campaign?.budget_spent ?? 0) || 0;

    return Math.round(currentBudget - initialTargetBudget + targetBudget) || 0;
  }, [
    campaign?.reach_campaigns,
    campaign?.budget_spent,
    initialTargetBudget,
    targetBudget,
    overlapDuration,
    remainingDurationBudget,
  ]);

  /**
   * Recalculates the average expected CPA when one publisher's expected CPA is updated.
   *
   * Formula:
   * newExpectedCpa = (currentAvg * totalCampaigns - oldValue + newValue) / totalCampaigns
   */
  const expectedCpa = useMemo(() => {
    if (noOfCampaigns === 0) return 0;

    return (
      _round(
        (campaignExpectedCpa * noOfCampaigns -
          selectedPublisherExpectedCpa +
          inputExpectedCpa) /
          noOfCampaigns,
        2,
      ) || 0
    );
  }, [
    campaignExpectedCpa,
    noOfCampaigns,
    selectedPublisherExpectedCpa,
    inputExpectedCpa,
  ]);

  /**
   * Recalculates the average expected conversion when one publisher's expected conversion is updated.
   *
   * Formula:
   * newExpectedConversion = (currentAvg * totalCampaigns - oldValue + newValue) / totalCampaigns
   */
  const expectedConversion = useMemo(() => {
    if (noOfCampaigns === 0) return 0;

    return (
      _round(
        (campaignExpectedConversion * noOfCampaigns -
          selectedPublisherExpectedConversion +
          inputExpectedConversion) /
          noOfCampaigns,
        2,
      ) || 0
    );
  }, [
    campaignExpectedConversion,
    noOfCampaigns,
    selectedPublisherExpectedConversion,
    inputExpectedConversion,
  ]);

  const targetApplicants = useMemo(() => {
    if (expectedCpa === 0) return 0;

    return Math.round(budget / expectedCpa) || 0;
  }, [budget, expectedCpa]);

  const targetHires = useMemo(() => {
    if (expectedCpa === 0) return 0;

    return Math.round(((budget / expectedCpa) * expectedConversion) / 100) || 0;
  }, [budget, expectedCpa, expectedConversion]);

  /**
   * Recalculates the per-day budget when one publisher's budget is updated.
   *
   * Formula:
   * newPerDayBudget = (currentPerDayBudget - oldValue + newValue) / totalCampaigns
   */
  const perDayBudget = useMemo(() => {
    const currentPerDayBudget =
      campaign?.reach_campaigns.reduce((acc, publisher) => {
        return acc + publisher.budget;
      }, 0) || 0;

    return _round(
      currentPerDayBudget - (selectedPublisher?.budget ?? 0) + inputBudget,
      2,
    );
  }, [campaign?.reach_campaigns, inputBudget, selectedPublisher?.budget]);

  return {
    budget,
    expectedCpa,
    expectedConversion,
    targetApplicants,
    targetHires,
    perDayBudget,
  };
}
